<template>
  <fragment>
    <div class="input-group date">
      <div
        class="input-group-prepend"
        v-on:click="show"
        v-if="hasSlot('prepend')"
      >
        <slot name="prepend" />
      </div>
      <input
        :disabled="disabled"
        :readonly="readonly"
        ref="datepicker"
        type="text"
        :class="inputClassString"
        class="form-control form-datepicker"
        :value="formValue"
        :placeholder="placeholder"
        v-on:change="onChange"
      />
      <div
        class="input-group-append"
        v-on:click="show"
        v-if="hasSlot('append')"
      >
        <slot name="append" />
      </div>
    </div>
    <div class="invalid-feedback-fix" v-if="!isDateValid">
      {{ errorMessage }}
    </div>
  </fragment>
</template>

<style lang="scss">
.invalid-feedback-fix {
  width: 100%;
  margin-top: 0.25rem;
  font-size: 80%;
  color: #dc3545;
}

#datepickers-container {
  z-index: 99999999999;
}

.form-control.form-datepicker[readonly] {
  background-color: #fff;
}
</style>

<script>
import 'air-datepicker/dist/css/datepicker.min.css'
import 'air-datepicker/dist/js/datepicker.min.js'
import 'air-datepicker/dist/js/i18n/datepicker.en.js'

import Vue from 'vue'

export default {
  props: {
    value: {
      type: String,
      // required: true,
    },
    inputClass: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: 'Select date',
    },
    position: {
      type: String,
      default: 'bottom left', // http://t1m0n.name/air-datepicker/docs/#sub-section-14
    },
    minDate: {
      default: () => Vue.moment.utc().format(),
    },
    maxDate: {
      type: String,
      default: () => null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    updateOnScroll: {
      type: Boolean,
      default: false,
    },
    validate: {
      type: Function,
      default: null,
    },
    clearButton: {
      type: Boolean,
      default: false,
    },
    inputFormat: {
      type: String,
      default: '',
    },
    format: {
      type: String,
      default: 'YYYY-MM-DD',
    },
  },
  name: 'date-picker',
  data: () => ({
    formValue: '',
    errorMessage: '',
    selecting: false,
    initialized: false,
    datepickerElement: null,
  }),
  mounted() {
    const inputValue = this.value
      ? this.$moment.utc(this.value, this.inputFormat)
      : null
    this.formValue =
      inputValue && inputValue.isValid() ? inputValue.format(this.format) : ''
    this.$nextTick(() => this.initialize())
  },
  unmounted() {
    window.removeEventListener('scroll', this.onScroll)
  },
  methods: {
    hasSlot(name = 'default') {
      return !!this.$slots[name] || !!this.$scopedSlots[name]
    },
    onScroll() {
      if (!this.initialized) return
      if (!this.$refs.datepicker) return
      this.hide()
    },
    show() {
      if (!this.disabled) {
        $(this.$refs.datepicker).data('datepicker').show()
      }
    },
    hide() {
      $(this.$refs.datepicker).data('datepicker').hide()
    },
    initialize() {
      if (this.initialized) return

      $(this.$refs.datepicker).datepicker({
        language: 'en',
        position: this.position,
        // dateFormat: 'dd/mm-yyyy',
        dateFormat: this.format.toLowerCase(),
        clearButton: this.clearButton,
        onSelect: () => this.onSelect(),
        autoClose: false,
        onShow: (inst, ani) => this.onShow(inst, ani),
        onHide: (inst, ani) => this.onHide(inst, ani),
      })
      this.initialized = true
      this.$nextTick(() =>
        $($(this.$refs.datepicker).data('datepicker').nav.$buttonsContainer)
          .find('.datepicker--button[data-action="clear"]')
          .click(() => this.clearValue())
      )
      this.$nextTick(() => this.updateDatepicker())
      this.selecting = true
      $(this.$refs.datepicker).data('datepicker').selectDate(this.selectedDate)
      this.selecting = false
    },
    onShow(inst, ani) {
      if (!ani) {
        this.selecting = true
        this.$refs.datepicker.value = this.formValue
        $(this.$refs.datepicker)
          .data('datepicker')
          .selectDate(this.selectedDate)
        window.removeEventListener('scroll', this.onScroll)
        window.addEventListener('scroll', this.onScroll)
      } else {
        this.selecting = false
      }
    },
    onHide(inst, ani) {
      if (ani) return
      this.formValue = this.$refs.datepicker.value
      if (this.formValue.trim() == '') {
        this.$emit('input', '')
      } else {
        const outputDate = this.$moment.utc(this.formValue, this.format)
        if (outputDate.isValid()) {
          // Set specific time mitigate timezone issues with utc.
          this.$emit(
            'input',
            outputDate
              .set({ hour: 12, minute: 34, second: 56 })
              .format(this.inputFormat)
          )
        }
      }
      window.removeEventListener('scroll', this.onScroll)
    },
    clearValue() {
      this.formValue = this.$refs.datepicker.value = ''
      this.hide()
    },
    onChange() {
      this.hide()
    },
    onSelect() {
      if (this.selecting) return
      if (this.$refs.datepicker.value.trim() == '') return
      this.hide()
    },
    updateDatepicker() {
      if (!$(this.$refs.datepicker).data('datepicker')) return

      const minDate = this.$moment.utc(this.minDate, "YYYY-MM-DD")
      const maxDate = this.$moment.utc(this.maxDate, "YYYY-MM-DD")

      $(this.$refs.datepicker)
        .data('datepicker')
        .update({
          minDate:
            this.minDate === false
              ? null
              : minDate.isValid()
              ? minDate.toDate()
              : new Date(),
          maxDate: maxDate.isValid() ? maxDate.toDate() : null,
          // startDate: this.selectedDate,
        })
      $(this.$refs.datepicker).data('datepicker').selectDate(this.selectedDate)
    },
  },
  computed: {
    inputClassString() {
      return this.inputClass + (!this.isDateValid ? ' is-invalid' : '')
    },
    isDateValid() {
      if (this.formValue.trim() == '') return true
      if (!this.$moment.utc(this.formValue, this.format).isValid()) {
        this.errorMessage = `Please enter a valid date (${this.format})`
        return false
      }
      if (this.validate) {
        const response = this.validate(this.formValue)
        this.errorMessage = response ? response : ''
        return response ? false : true
      }
      return true
    },
    selectedDate() {
      return this.formValue &&
        this.$moment.utc(this.formValue, this.format).isValid()
        ? this.$moment.utc(this.formValue, this.format).toDate()
        : null
    },
  },
  watch: {
    value: function (new_value, old_value) {
      const inputValue = this.value
        ? this.$moment.utc(this.value, this.inputFormat)
        : null
      this.formValue =
        inputValue && inputValue.isValid() ? inputValue.format(this.format) : ''
      this.updateDatepicker()
    },
    minDate: function (new_value, old_value) {
      this.updateDatepicker()
    },
    maxDate: function (new_value, old_value) {
      this.updateDatepicker()
    },
  },
}
</script>
